import { Box, Circle, HStack, SimpleGrid, Tag, Text, VStack, Wrap } from '@chakra-ui/react'
import type { Location, PrescriberProfile } from '../../../types/location'
import { formatLocationSuburb, formatPrescriberName } from '../../../types/location'

interface LocationDetailPanelProps {
  location: Location
  prescribers: PrescriberProfile[]
}

const DetailLabel = ({ children }: { children: string }) => (
  <Text
    fontSize="xs"
    fontWeight="bold"
    textTransform="uppercase"
    letterSpacing="wider"
    color="gray.500"
    mb={1}
  >
    {children}
  </Text>
)

export const LocationDetailPanel = ({ location, prescribers }: LocationDetailPanelProps) => {
  const addressLine1 = location.address1 ?? ''
  const addressLine2 = formatLocationSuburb(location)

  return (
    <VStack spacing={5} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        <Box>
          <DetailLabel>Address</DetailLabel>
          {addressLine1 && (
            <Text fontSize="sm" color="gray.800">
              {addressLine1}
            </Text>
          )}
          {addressLine2 && (
            <Text fontSize="sm" color="gray.800">
              {addressLine2}
            </Text>
          )}
          {!addressLine1 && !addressLine2 && (
            <Text fontSize="sm" color="gray.400">
              —
            </Text>
          )}
        </Box>

        <Box>
          <DetailLabel>Status</DetailLabel>
          <HStack spacing={2}>
            <Circle size="8px" bg={location.status ? 'green.400' : 'gray.300'} />
            <Text fontSize="sm" color="gray.800">
              {location.status ? 'Active' : 'Inactive'}
            </Text>
          </HStack>
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        <Box>
          <DetailLabel>Phone</DetailLabel>
          <Text fontSize="sm" color="gray.800">
            {location.phone || '—'}
          </Text>
        </Box>

        <Box>
          <DetailLabel>Email</DetailLabel>
          <Text fontSize="sm" color="gray.800">
            {location.email || '—'}
          </Text>
        </Box>
      </SimpleGrid>

      {prescribers.length > 0 && (
        <Box>
          <DetailLabel>{`Prescribers (${prescribers.length})`}</DetailLabel>
          <Wrap spacing={2} mt={1}>
            {prescribers.map((prescriber) => (
              <Tag
                key={prescriber.id}
                size="md"
                variant="outline"
                borderRadius="md"
                borderColor="gray.200"
                color="gray.700"
                fontWeight="medium"
                px={3}
                py={1.5}
              >
                {formatPrescriberName(prescriber)}
              </Tag>
            ))}
          </Wrap>
        </Box>
      )}
    </VStack>
  )
}
